import React, { useEffect, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import Dropdown from "../DropDown";
import { fetchWithCache } from "../../CacheDB/cached-Ref-Api";

interface BETMObjectData {
  BETM_OBJECTKEY: number;
  OBJECT_NAME: string;
}

interface BETMObjectDropdownProps {
  selectedBETMObjectKey: number | string;
  onBETMObjectChange: (selectedName: string, selectedKey: number) => void;
  disabled: boolean;
  className?: string;
  onFocus?: () => void;
}

const BETMObjectKeyDropdown: React.FC<BETMObjectDropdownProps> = ({
  selectedBETMObjectKey,
  onBETMObjectChange,
  disabled,
  className = "",
  onFocus,
}) => {
  const [betmObjectData, setBETMObjectData] = useState<BETMObjectData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      const startTime = performance.now();
      try {
        const data = await fetchWithCache("betm-object-kv", "/ref-data/betm-object-kv");
        setBETMObjectData(data);

        datadogRum.addAction("Fetched BETM Objects (Cached/Live)", {
          objectCount: data.length,
        });
      } catch (err) {
        setError("Failed to load BETM Objects");
        datadogRum.addError("BETM Object Fetch Failed", { error: err });
      } finally {
        setLoading(false);
        const endTime = performance.now();
        datadogRum.addAction("BETM Object Fetch Duration", {
          duration: endTime - startTime,
        });
      }
    };

    loadData();
  }, []);

  const options = betmObjectData.map((item) => ({
    value: item.BETM_OBJECTKEY.toString(),
    label: item.OBJECT_NAME,
  }));

  const handleChange = (value: string) => {
    const selected = betmObjectData.find((obj) => obj.BETM_OBJECTKEY.toString() === value);
    if (selected) {
      onBETMObjectChange(selected.OBJECT_NAME, selected.BETM_OBJECTKEY);
      datadogRum.addAction("BETM Object Selected", selected);
    }
  };

  return (
    <Dropdown
      options={options}
      selectedValue={selectedBETMObjectKey?.toString() ?? ""}
      onValueChange={handleChange}
      disabled={disabled || loading || !!error}
      className={className}
      placeholder={loading ? "Loading BETM Objects..." : error ?? "Select BETM Object"}
      onFocus={onFocus}
    />
  );
};

export default BETMObjectKeyDropdown;